import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  LayoutGrid,
  Maximize,
  Minimize,
  Share2,
  FileDown,
  UserCog,
  X,
} from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { renderSlide } from "./slides";
import { EASE } from "./slides/shared";

const slideVariants = {
  enter: (d) => ({ x: d >= 0 ? "6%" : "-6%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d >= 0 ? "-6%" : "6%", opacity: 0 }),
};

export default function Deck({ slides }) {
  const [[index, dir], setNav] = useState([0, 0]);
  const [gridOpen, setGridOpen] = useState(false);
  const [isFs, setIsFs] = useState(false);
  const lockRef = useRef(false);
  const touchX = useRef(null);
  const navigate = useNavigate();
  const total = slides.length;
  const slide = slides[index];

  const go = useCallback(
    (d) => {
      setNav(([i]) => {
        const n = Math.min(Math.max(i + d, 0), total - 1);
        return n === i ? [i, 0] : [n, d];
      });
    },
    [total]
  );

  const goTo = useCallback((n) => {
    setNav(([i]) => [n, n > i ? 1 : -1]);
    setGridOpen(false);
  }, []);

  const toggleFs = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen().catch(() => {});
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setGridOpen(false);
        return;
      }
      if (gridOpen) return;
      if (["ArrowRight", "ArrowDown", " ", "PageDown"].includes(e.key)) {
        e.preventDefault();
        go(1);
      } else if (["ArrowLeft", "ArrowUp", "PageUp"].includes(e.key)) {
        e.preventDefault();
        go(-1);
      } else if (e.key.toLowerCase() === "f") toggleFs();
      else if (e.key === "Home") goTo(0);
      else if (e.key === "End") goTo(total - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, goTo, gridOpen, toggleFs, total]);

  useEffect(() => {
    const onWheel = (e) => {
      if (gridOpen || lockRef.current || Math.abs(e.deltaY) < 24) return;
      lockRef.current = true;
      setTimeout(() => (lockRef.current = false), 900);
      go(e.deltaY > 0 ? 1 : -1);
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    return () => window.removeEventListener("wheel", onWheel);
  }, [go, gridOpen]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin + "/");
      toast.success("Link di condivisione copiato");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div
      className="no-print h-[100dvh] flex flex-col bg-white overflow-hidden"
      onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}
      data-testid="deck-root"
    >
      {/* Header */}
      <header className="relative z-50 h-16 shrink-0 px-4 sm:px-6 flex items-center justify-between border-b border-line bg-white/80 backdrop-blur-md">
        <Logo />
        <div
          className="hidden md:block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500"
          data-testid="slide-breadcrumb"
        >
          {pad(index + 1)} — {slide.nav}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            data-testid="share-copy-button"
            title="Copia link di condivisione"
            onClick={(e) => {
              e.currentTarget.blur();
              copyLink();
            }}
            className="p-2.5 rounded-full text-slate-500 hover:text-ink hover:bg-mist transition-colors"
          >
            <Share2 size={17} />
          </button>
          <button
            data-testid="pdf-export-button"
            title="Esporta in PDF"
            onClick={(e) => {
              e.currentTarget.blur();
              window.print();
            }}
            className="p-2.5 rounded-full text-slate-500 hover:text-ink hover:bg-mist transition-colors"
          >
            <FileDown size={17} />
          </button>
          <button
            data-testid="admin-link-button"
            title="Pannello admin"
            onClick={(e) => {
              e.currentTarget.blur();
              navigate("/admin");
            }}
            className="p-2.5 rounded-full text-slate-500 hover:text-ink hover:bg-mist transition-colors"
          >
            <UserCog size={17} />
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-line/60">
          <motion.div
            data-testid="progress-bar"
            className="h-full bg-brand"
            animate={{ width: `${((index + 1) / total) * 100}%` }}
            transition={{ duration: 0.5, ease: EASE }}
          />
        </div>
      </header>

      {/* Slide stage */}
      <div className="relative flex-1 overflow-hidden">
        <AnimatePresence mode="wait" custom={dir} initial={false}>
          <motion.div
            key={slide.id}
            custom={dir}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.45, ease: EASE }}
            className="absolute inset-0"
          >
            {renderSlide(slide, { onNext: () => go(1) })}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dock */}
      <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 bg-white/90 backdrop-blur-md border border-line shadow-xl shadow-slate-200/60 rounded-full px-3 py-2 flex items-center gap-1">
        <button
          data-testid="prev-slide-button"
          title="Slide precedente"
          disabled={index === 0}
          onClick={(e) => {
            e.currentTarget.blur();
            go(-1);
          }}
          className="p-2.5 rounded-full text-ink hover:bg-mist transition-colors disabled:opacity-30"
        >
          <ArrowLeft size={17} />
        </button>
        <span
          data-testid="slide-counter"
          className="font-mono text-xs text-slate-500 px-2 select-none tabular-nums"
        >
          {pad(index + 1)} / {pad(total)}
        </span>
        <button
          data-testid="next-slide-button"
          title="Slide successiva"
          disabled={index === total - 1}
          onClick={(e) => {
            e.currentTarget.blur();
            go(1);
          }}
          className="p-2.5 rounded-full text-ink hover:bg-mist transition-colors disabled:opacity-30"
        >
          <ArrowRight size={17} />
        </button>
        <span className="w-px h-5 bg-line mx-1" />
        <button
          data-testid="slide-grid-toggle"
          title="Panoramica slide"
          onClick={(e) => {
            e.currentTarget.blur();
            setGridOpen(true);
          }}
          className="p-2.5 rounded-full text-ink hover:bg-mist transition-colors"
        >
          <LayoutGrid size={17} />
        </button>
        <button
          data-testid="fullscreen-toggle"
          title="Schermo intero (F)"
          onClick={(e) => {
            e.currentTarget.blur();
            toggleFs();
          }}
          className="p-2.5 rounded-full text-ink hover:bg-mist transition-colors"
        >
          {isFs ? <Minimize size={17} /> : <Maximize size={17} />}
        </button>
      </div>

      {/* Grid overview */}
      <AnimatePresence>
        {gridOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 bg-white/95 backdrop-blur-md overflow-y-auto"
            data-testid="grid-overview"
          >
            <div className="max-w-6xl mx-auto px-6 py-10">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-400">Panoramica</div>
                  <h2 className="font-display font-extrabold text-2xl tracking-tight text-ink mt-1">
                    Tutte le slide
                  </h2>
                </div>
                <button
                  data-testid="grid-close-button"
                  onClick={() => setGridOpen(false)}
                  className="p-2.5 rounded-full hover:bg-mist transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {slides.map((s, i) => (
                  <button
                    key={s.id}
                    data-testid={`grid-thumb-${s.id}`}
                    onClick={() => goTo(i)}
                    className={`text-left border rounded-xl p-4 transition-colors ${
                      i === index ? "border-brand bg-brand-subtle/40" : "border-line bg-white hover:border-slate-400"
                    }`}
                  >
                    <div className={`font-mono text-xs ${i === index ? "text-brand" : "text-slate-400"}`}>
                      {pad(i + 1)}
                    </div>
                    <div className="mt-2 font-display font-semibold text-sm text-ink leading-snug">{s.nav}</div>
                    <div className="mt-1 text-[11px] text-slate-400 line-clamp-2">{s.title}</div>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
